import { useState, useRef, useEffect, forwardRef, useContext } from "react";
import Swal from "sweetalert2";

import { hexToRgb, shadeColor } from "../utilities/Color";
import { convertIntuitiveTime } from "../utilities/Time";
import { getImage } from "../utilities/Links";
import { readImage } from "../utilities/Files";

import AuthContext from '../../components/js/modules/AuthContext';
import * as MessagingHandler from "../../components/js/modules/Messaging";

import * as LoadingMarkers from "./LoadingMarkers"
import Button from "./Button";
import TextArea from "./TextArea";
import Loading from "./Loading";
import Error from "./Error";

import "../css/Chat.css";
import "../css/Messages.css";

const getTicketContributors = (ticket) => {
    var contributors = {};
    for (var i = 0; i < ticket.messages.length; i++) {
        var message = ticket.messages[i];
        if (!contributors[message.user.rbxuser.username] && message.user.rbxuser.username) {
            contributors[message.user.rbxuser.username] = message.user;
        }
    }

    contributors[ticket.account.rbxuser.username] = ticket.account;

    return contributors;
}

const isActiveViewer = (viewers, username) => {
    for (const viewer of viewers) {
        if (viewer.username == username) {
            return true;
        }
    }
    return false;
}

const Mentions = forwardRef(({
    visibleMentions,
    chatViewers,

    mentionSearch,
    setMentionMenu,

    setMessage
}, textareaRef) => {
    const select = (user) => {
        setMessage((old) => {
            if (mentionSearch.length >= 1) {
                return old.replace(`@${mentionSearch}`, `@${user.rbxuser.username}`);
            } else {
                if (textareaRef) {
                    var cursorIndex = textareaRef.current.selectionStart;
                    var insertAt = cursorIndex - 1;

                    var firstHalf = old.substring(0, insertAt);
                    var secondHalf = old.substring(cursorIndex);

                    return `${firstHalf}@${user.rbxuser.username}${secondHalf}`
                }
            }
        });

        setMentionMenu(false);
    }

    return <div className="messenger-mentions">
        <span>{mentionSearch ? `USERS MATCHING @${mentionSearch.toUpperCase()}` : "USERS"}</span>

        <div className="messenger-mentions-list">
            {Object.keys(visibleMentions).length == 0 && <span>No results...</span>}
            {Object.values(visibleMentions).map((user) => {
                var isOnline = isActiveViewer(chatViewers, user.rbxuser.username);

                return <div 
                    className="messenger-mention"
                    onClick={() => select(user)}
                >
                    <div className="messenger-mention-img">
                        <img src={user.rbxuser.imageurl}></img>
                        <div data-status={isOnline ? "active" : "offline"} id="mention-activity"></div>
                    </div>
                    <span>{user.rbxuser.username}</span>
                </div>
            })}
        </div>
    </div>
});

const Attachment = ({
    file,
    onDeleted
}) => {
    const [attachment, setAttachment] = useState()

    useEffect(() => {
        readImage(file, (attachment) => {
            setAttachment(attachment);
        });
    }, [file])

    return attachment && <div className="messenger-attachment">
        <Button
            data-animation="raise"
            onClick={onDeleted}
        >
            <i className={`fa-solid fa-trash`}></i>
        </Button>
    
        <img
            src={attachment}
            title={file.name}
        />
        <span>{file.name}</span>
    </div>
}

const Attachments = ({
    attachments,
    setAttachments
}) => {
    return <div className="messenger-attachments">
        <span>ATTACHMENTS</span>

        <div className="messenger-attachments-container">
            {attachments.map((attachment, key) => {
                return <Attachment
                    file={attachment}
                    onDeleted={() => {
                        setAttachments((old) => {
                            old.splice(key, 1);
                            return [...old];
                        });
                    }}
                />
            })}
        </div>
    </div>
}

const Messenger = ({
    chatViewers,

    contributors,

    visibleMentions,
    setVisibleMentions
}) => {
    const [message, setMessage] = useState();
    const [status, setStatus] = useState("available");
    const [replyType, setReplyType] = useState("reply");
    const [attachments, setAttachments] = useState([]);

    const [mentionMenu, setMentionMenu] = useState(false);
    const [mentionSearch, setMentionSearch] = useState();

    const textareaRef = useRef(null);

    const parseMentions = () => {
        const mentionRegex = /@(\w+)/g;
        const mentions = [];

        for (const match of message.matchAll(mentionRegex)) {
            if (!mentions.includes(match[1])) {
                mentions.push(match[1]);
            }
        }

        return mentions;
    }

    const uploadAttachments = async () => {
        return await MessagingHandler.upload(attachments);
    }

    const send = async () => {
        if (!message || status != "available") return;
        if (message.length == 0 || message == " ") {
            return Swal.fire({
                title: "Error",
                text: "Empty messages can not be sent.",
                icon: "error",
                confirmButtonText: "Ok"
            });
        }
        if (message.length > 2000) {
            return Swal.fire({
                title: "Error",
                text: "Messages can not exceed 2,000 characters.",
                icon: "error",
                confirmButtonText: "Ok"
            });
        }
        
        var mentions = parseMentions();
        if (mentions.length > 10) {
            return Swal.fire({
                title: "Error",
                text: "Messages can not exceed 10 mentions.",
                icon: "error",
                confirmButtonText: "Ok"
            });
        }

        // Start Message Processing
        var messageBody = {
            content: message,
            replyType,
            mentions: mentions,
            notify: false
        };

        if (attachments.length > 0 && attachments.length <= 5) {
            setStatus("Processing Attachments");
            const uploadResponse = await uploadAttachments();

            if (uploadResponse.message != "Success") {
                Swal.fire({title: 'Error', text: 'One of your attachment(s) is too large or an unsupported file type. The upload limit is 10MB.', icon: 'error', confirmButtonText: 'Ok'});
                setStatus("available");
                return;
            } else {
                messageBody.attachments = uploadResponse.data;
            }
        }

        setStatus("Sending Message");

        MessagingHandler.publishMessage(messageBody, (res) => {
            if (res.status == "ok") {
                setMentionMenu(false);
                setMessage("");
                setAttachments([]);
            } else {
                Swal.fire({
                    title: "Error",
                    text: "Failed to send your message, refresh and try again. If the issue persists, notify the Network Engineer.",
                    icon: "error",
                    confirmButtonText: "Ok"
                });
            }
            setStatus("available");
        });
    }

    const mentionCmd = (arg) => {
        setMentionSearch(arg);
        setMentionMenu(true);
    }

    const handleCursorMove = () => {
        if (textareaRef.current && message) {
            const mentionRegex = /@(\w+)/g;
            const cursorIndex = textareaRef.current.selectionStart;

            for (const match of message.matchAll(mentionRegex)) {
                var start = match.index;
                var end = start + match[0].length;

                if (cursorIndex >= start && cursorIndex <= end && !Object.keys(contributors).includes(match[1])) {
                    return mentionCmd(match[1]);
                }
            }

            if (message.charAt(cursorIndex - 1) == "@") {
                setVisibleMentions(Object.assign({}, contributors));
                setMentionMenu(true);
                return;
            } else {
                setMentionMenu(false);
            }
        }
        setMentionMenu(false);
    }

    const markAttachment = (e) => {
        if (e.target.files.length == 0) return;
        if (e.target.files.length > 5) {
            Swal.fire({title: "Error", text: "You have the maximum amount of attachments uploaded.", icon: "error", confirmButtonText: "Ok"});
            return;
        }
        if (attachments.length >= 5) {
            Swal.fire({title: "Error", text: "You have the maximum amount of attachments uploaded.", icon: "error", confirmButtonText: "Ok"});
            return;
        }

        setAttachments((state) => {
            return [...e.target.files, ...state]
        });
    }

    useEffect(() => {
        if (!mentionSearch || mentionSearch == "" || mentionSearch == " ") {
            return setVisibleMentions(Object.assign({}, contributors));
        }

        var arr = Object.values(contributors);
        setVisibleMentions((old) => {
            for (var i = 0; i < arr.length; i++) {
                var contributor = arr[i];
                if (contributor.rbxuser.username.toLowerCase().includes(mentionSearch.toLowerCase())) {
                    old[contributor.rbxuser.username] = contributor;
                } else {
                    if (Object.hasOwn(old, contributor.rbxuser.username)) {
                        delete old[contributor.rbxuser.username];
                    }
                }
            }
            return Object.assign({}, old);
        });
    }, [mentionSearch]);

    useEffect(() => {
        if (!mentionMenu) {
            setMentionSearch("");
        }
    }, [mentionMenu]);

    return <div className="messenger">
        {status != "available" && <div className="messenger-row messenger-status-bar">
            <LoadingMarkers.PulsingDots/>
            <span>{status}</span>
        </div>}

        <div className="messenger-row messenger-reply-type">
            <Button 
                data-status={replyType == "reply" ? "selected" : "unselected"}
                data-selectedcolor="blue"
                data-type="basic"

                onClick={() => {
                    if (status != "available") return;
                    setReplyType("reply");
                }}
            >
                <i className="fa-solid fa-paper-plane"></i> Reply
            </Button>

            <Button 
                data-status={replyType == "note" ? "selected" : "unselected"}
                data-selectedcolor="orange"
                data-type="basic"

                onClick={() => {
                    if (status != "available") return;
                    setReplyType("note");
                }}
            >
                <i className="fa-solid fa-note-sticky"></i> Staff Note
            </Button>

            <div style={{marginLeft: "auto", marginRight: "8px"}} className="messenger-row messenger-icon-btns">
                <Button
                    data-status="unselected"
                    data-type="basic"
                    data-icon={true}

                    title="Mention"

                    onClick={() => {
                        if (status != "available") return;
                        setMessage((old) => {
                            if (old) {
                                var lastChar = old.charAt(old.length - 1);
                                if (lastChar == "" || lastChar == " ") {
                                    return `${old}@`;
                                } else {
                                    return `${old} @`;
                                }
                            } else {
                                return "@";
                            }
                        });

                        if (textareaRef.current) {
                            textareaRef.current.select();
                        }
                    }}
                >
                    <i className="fa-solid fa-at"></i>
                </Button>

                <input type="file" id="message-attachment-upload" hidden onChange={markAttachment} accept='.jpg, .jpeg, .png, .gif, .webp, .mp4, .quicktime'/>
                <Button
                    data-status="unselected"
                    data-type="basic"
                    data-icon={true}

                    title="Upload Attachment"

                    onClick={() => {
                        if (attachments.length >= 5) {
                            Swal.fire({title: "Error", text: "You have the maximum amount of attachments uploaded.", icon: "error", confirmButtonText: "Ok"});
                        } else {
                            document.getElementById("message-attachment-upload").click();
                        }
                    }}
                >
                    <i className="fa-solid fa-link"></i>
                </Button>
            </div>
        </div>

        <div className="messenger-text-container">
            {attachments.length > 0 && <Attachments
                attachments={attachments}
                setAttachments={setAttachments}
            />}
            
            {mentionMenu && <Mentions
                visibleMentions={visibleMentions}
                chatViewers={chatViewers}

                mentionSearch={mentionSearch}
                setMentionMenu={setMentionMenu}

                ref={textareaRef}
                setMessage={setMessage}
            />}
            
            <TextArea
                ref={textareaRef}

                placeholder="Write a message..."

                setState={setMessage}
                onKeyUp={handleCursorMove}
                onMouseDown={handleCursorMove}
                onSelect={handleCursorMove}

                disabled={status != "available"}

                onPaste={(e) => {
                    const items = e.clipboardData?.items;
                    if (!items) return;

                    for (const item of items) {
                        if (item.type.includes("image")) {
                            const file = item.getAsFile();
                            if (file) {
                                e.preventDefault();
                                markAttachment({
                                    target: {
                                        files: [file]
                                    }
                                });

                                break;
                            }
                        }
                    }
                }}

                onKeyDown={(e) => {
                    if (e.key == "Enter") {
                        if (!e.shiftKey) {
                            e.preventDefault();
                            send();
                        } else {
                            setMessage((old) => `${old}\n`);
                        }
                    }
                }}

                maxCharacters={2000}
            >
                {message}
            </TextArea>
        </div>

        <span className="messenger-type-notice">
            <i style={{marginRight: "8px", color: "#ee8449"}} className="fa-solid fa-circle-exclamation"></i>
            {replyType == "reply" ? "All users will be able to see this message" : "Only staff members will be able to see this message"}
        </span>
    </div>
}

const Notice = ({
    notice
}) => {
    return <div style={{borderLeft: `4px solid ${notice.color}`}} className="message-notice">
        {notice.sections.map((section) => {
            return <div className="message-notice-section">
                <h3><i className={section.logo}/> {section.title}</h3>

                {section.fields.map((field) => {
                    return <span>{field.content}</span>
                })}
            </div>
        })}
    </div>
}

const Message = ({
    ticketData,
    message,

    onDelete
}) => {
    const [attachments, setAttachments] = useState([]);
    const [hovering, setHovering] = useState(false);

    const authContext = useContext(AuthContext);

    const mentionRegex = /@(\w+)/g;
    const linkRegex = /^https:\/\/\S+$/i;
 
    var mentionsMe = message.mentions && message.mentions.includes(authContext.user.rbxuser.username);
    var isStaff = message.visibleToPermLevel > 1;
    
    var title = false;
    var titleColor = "";
    var titleIcon = ""
    if (message.user.permLevel > 1) {
        title = message.user.permissionName;
        titleColor = "238, 132, 73";
        titleIcon = "fa-solid fa-shield-halved";
    } else if (message.user.account_id == "Bot") {
        title = "Automation";
        titleColor = "52, 159, 201";
        titleIcon = "fa-solid fa-gear";
    }

    const redirect = (url) => {
        if (!url.startsWith("https://supportapi.romestaff.com")) {
            Swal.fire({
                title: "Are you sure?",
                text: "The integrity of this link cannot be verified by the system.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#34c940",
                cancelButtonColor: "#c93434",
                confirmButtonText: "Confirm"
            }).then(async (result) => {
                if (result.isConfirmed) {
                    window.open(url, "_blank", "noopener,noreferrer");
                }
            });
        } else {
            window.open(url, "_blank", "noopener,noreferrer");
        }
    }

    useEffect(() => {
        (async () => {
            const imageUrls = [];
            for (const str of message.content.split(" ")) {
                if (linkRegex.test(str)) {
                    const imageUrl = await getImage(str)
                    if (imageUrl) {
                        imageUrls.push(imageUrl);
                    }
                }
            }

            for (const attachment of message.attachments) {
                imageUrls.push(attachment.url);
            }

            setAttachments(imageUrls);
        })()
    }, [message]);

    return <div
        style={{
            backgroundColor: mentionsMe ? "rgba(206, 156, 92, 0.3)" : isStaff && "rgba(201, 52, 52, 0.3)",
            borderLeft: mentionsMe ? "4px solid rgba(206, 156, 92, 1)" : isStaff && "4px solid rgba(201, 52, 52, 1)"
        }}
        className="message"

        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
    >
        <img className="message-profile-pic" src={message.user.rbxuser.imageurl || "/images/staff-logo-new.png"}/>

        {hovering && <div className="message-actions">
            {ticketData.user_permissions.canDeleteMessages &&
                <Button
                    data-animation="raise"
                    onClick={onDelete}
                >
                    <i className={`fa-solid fa-trash`}></i>
                </Button>
            }
        </div>}

        <div className="message-column message-content">
            <div className="message-row">
                <span className="message-profile-name">
                    {message.user.rbxuser.username || "Roman Systems"}
                </span>

                {title && <span style={{backgroundColor: `rgba(${titleColor}, 0.3)`, border: `1px solid rgba(${titleColor})`}} className="message-profile-title"><i className={titleIcon}/> {title}</span>}
                {isStaff && <span style={{backgroundColor: `rgba(238, 132, 73, 0.3)`, border: `1px solid rgba(238, 132, 73)`}} className="message-profile-title"><i className="fa-solid fa-note-sticky"/> Staff Note</span>}

                <span className="message-profile-time">
                    {convertIntuitiveTime(message.time)}
                </span>
            </div>

            <span
                style={{marginTop: title && "2px" || "0px"}}
                className="message-text"
            >
                {message.content.split(" ").map((str) => {
                    if (mentionRegex.test(str) && message.mentions && message.mentions.includes(str.substring(1, str.length))) {
                        return <><span className="message-mention">{str}</span>{" "}</>
                    } else if (linkRegex.test(str)) {
                        return <>
                            <a className="message-link" href={str} target="_blank">{str}</a>
                            {" "}
                        </>
                    } else {
                        return `${str} `
                    }
                })}
            </span>

            {message.notices.length > 0 && <div className="message-notices">
                {message.notices.map((notice) => {
                    return <Notice
                        notice={notice}
                    />
                })}
            </div>}

            <div className="message-column message-attachments">
                {attachments.map((url) => {
                    return <img
                        onClick={() => {
                            redirect(url);
                        }}
                        className="message-attachment" 
                        src={url}
                    />
                })}
            </div>
        </div>
    </div>
}

const Messages = ({
    ticketData,
    messages
}) => {
    const messageContainerRef = useRef();

    useEffect(() => {
        if (messageContainerRef.current) {
            messageContainerRef.current.scrollTop = 0;
        }
    }, [messages]);

    return <div className="messages-wrapper">
        <div 
            ref={messageContainerRef} 
            className="messages-container"
        >
            {messages.map((message) => {
                return <Message 
                    ticketData={ticketData} 
                    message={message}

                    onDelete={() => {
                        Swal.fire({
                            title: "Are you sure?",
                            text: "This action cannot be undone.",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonColor: "#34c940",
                            cancelButtonColor: "#c93434",
                            confirmButtonText: "Confirm"
                        }).then(async (result) => {
                            if (result.isConfirmed) {
                                MessagingHandler.deleteMessage(message.message_id, (res) => {
                                    if (res.status != 'ok') {
                                        Swal.fire({
                                            title: "Error",
                                            text: "There was an issue while trying to delete this message, it has either already been deleted or you do not have the proper permissions to delete it.",
                                            icon: "error",
                                            confirmButtonText: "Ok"
                                        });
                                    };
                                });
                            }
                        });
                    }}
                />
            })}

            {messages.length == 0 && <span className="messages-empty">👻 It's quiet in here...</span>}

            {messages.length > 0 &&
                <div className="messages-start">
                    <span>This is the beginning of the chat.</span>
                </div>
            }
        </div>
    </div>
}

const Chat = ({
    status, 
    ticketData,
    chatViewers,
    hidingInfo, 
    setHidingInfo, 
    messages 
}) => {
    const [visibleMentions, setVisibleMentions] = useState();
    const [contributors, setContributors] = useState();

    var panelColor = hexToRgb(ticketData.panel.color);
    var colorString = `rgba(${panelColor.r}, ${panelColor.g}, ${panelColor.b}`;

    const ticketProgress = ticketData.status == "Archived" && "Complete" || (ticketData.status == "Active" && (ticketData.staff_ids.length == 0 && "Awaiting Staff" || "Resolving"));
    const progressColor = (ticketProgress == "Complete" && "#34c940") || (ticketProgress == "Resolving" && "#ee8449") || (ticketProgress == "Awaiting Staff" && "#c93434");
    const progressIcon = (ticketProgress == "Complete" && "fa-solid fa-circle-check") || (ticketProgress == "Resolving" && "fa-solid fa-stopwatch") || (ticketProgress == "Awaiting Staff" && "fa-solid fa-clock");

    useEffect(() => {
        setContributors(getTicketContributors(ticketData));
        setVisibleMentions(contributors);
    }, [messages]);

    return <div data-hidinginfo={hidingInfo} className="chat-page">
        <Loading visible={status == "loading"} noBackground={false} text="Connecting"/>
        <Error visible={status == "error"} noBackground={false} text="There was an issue connecting to this ticket chat."></Error>

        {status == "loaded" && <>
            <div className="chat-header">
                <div className="chat-header-column">
                    <div style={{alignItems: "center"}} className="chat-header-row">
                        <h2>{ticketData.topic}</h2>
                        <Button
                            style={{marginLeft: "auto", padding: "9px 11px"}}
                            data-animation="raise"
                            
                            onClick={() => setHidingInfo((old) => !old)}
                        >
                            <i className={`fa-solid fa-chevron-${!hidingInfo ? "right" : "left"} fa-2x`}></i>
                        </Button>
                    </div>

                    <div style={{alignItems: "center"}} className="chat-header-row">
                        <span className="chat-header-id"><i className="fa-solid fa-envelope"></i> {ticketData.ticket_id.substring(0, 4)}</span>
                        <div className="vertical-line"></div>
                        <span style={{border: `1px solid ${ticketData.panel.color}`, backgroundColor: `${colorString}, 0.3)`}} className="chat-header-system">{ticketData.panel.title}</span>

                        <span style={{backgroundColor: progressColor, border: `1px solid ${shadeColor(progressColor, -35)}`}} className="chat-header-status"><i className={`${progressIcon} fa-sm`}></i> {ticketProgress}</span>
                    </div>
                </div>
            </div>

            <Messages
                ticketData={ticketData}
                messages={messages}
            />

            {ticketData.user_permissions.canChat ? <Messenger
                visibleMentions={visibleMentions}
                setVisibleMentions={setVisibleMentions}

                contributors={contributors}

                ticketData={ticketData}
                chatViewers={chatViewers}
            /> : <div className="messenger-archived-container">
                <h2 className="messenger-archived-notice">This ticket has been closed and archived</h2>
            </div>}
        </>}
    </div>
}

export default Chat;